import { useEffect, useRef, useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';

import { AccountsNav } from './components/accounts/AccountsNav';
import { ActivityNav } from './components/activity/ActivityNav';
import { BenefitsAndRewardsNav } from './components/benefits-and-rewards/BenefitsAndRewardsNav';
import { SidebarHeaderDocument } from './components/header/graphql/query.sidebarHeader.generated';
import { SidebarHeader } from './components/header/SidebarHeader';
import { LinkButton } from './components/LinkButton';
import { NavBackgroundScrim } from './components/NavBackgroundScrim';
import { SettingsNav } from './components/settings/SettingsNav';

import NavHeader from '../Shared/components-v2/nav-header/nav-header';
import { urlBuilder } from '../Shared/urlBuilder';

export default function Sidebar() {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('mouseup', handleClickOutside);
    document.addEventListener('touchend', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, [sidebarRef]);

  const { loading, data } = useGraphQLQuery(SidebarHeaderDocument, {
    fetchPolicy: 'cache-first',
    throwOnError: false,
  });

  const advisor = data?.advisor;
  const hasLogo = !loading && !advisor;

  return (
    <ErrorBoundary fallbackType="inline" feature="sidebar" owner="user_engagement">
      <NavHeader hasLogo={hasLogo} onHamburgerClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
      {isSidebarOpen && <NavBackgroundScrim />}

      <Box
        id="navigation-sidebar"
        ref={sidebarRef}
        css={{
          display: {
            base: isSidebarOpen ? 'block' : 'none',
            xl: 'block',
          },
          height: '100dvh',
          width: {
            base: '100vw',
            md: '240px',
          },
          minWidth: {
            base: '100vw',
            md: '240px',
          },
          position: {
            base: 'fixed',
            xl: 'sticky',
          },
          top: 0,
          overflow: 'auto',
          pb: 'space.300',
          px: 'space.100',
          zIndex: 5,
          backgroundColor: 'primitives.navy.100',
          _light: {
            backgroundColor: 'primitives.navy.100',
          },
          _dark: {
            backgroundColor: 'background.surface',
            md: {
              borderRightStyle: 'solid',
              borderRightWidth: 'thin',
              borderRightColor: 'border',
            },
          },
        }}
      >
        <SidebarHeader loading={loading} advisor={advisor} closeSidebar={() => setIsSidebarOpen(false)} />
        <ErrorBoundary fallbackType="inline" feature="sidebar" owner="user_engagement">
          <AccountsNav />
        </ErrorBoundary>
        <LinkButton to={urlBuilder.addGoal.absolute()}>Open new account</LinkButton>
        <ErrorBoundary fallbackType="inline" feature="sidebar" owner="user_engagement">
          <ActivityNav />
        </ErrorBoundary>
        <ErrorBoundary fallbackType="inline" feature="sidebar" owner="user_engagement">
          <BenefitsAndRewardsNav />
        </ErrorBoundary>
        <SettingsNav />
      </Box>
    </ErrorBoundary>
  );
}
